import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Switch from 'antd/lib/switch';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';

import {
  axiosDeleteCall,
  axiosGetCall,
  axiosPostCall,
} from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';

const { TextArea } = Input;
const { Option } = Select;

function EditProductModel({
  isOpen,
  product,
  handleEditModelCloseClick,
  callback,
  getAllProduct,
}) {
  let updatedProduct = { ...product };
  const [editForm] = Form.useForm();
  const { token } = useContext(AuthContext);
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [inputs, setInputs] = useState([{ key: '', value: '' }]);
  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productImages, setProductImages] = useState(
    updatedProduct.productImages || [],
  );
  const [isImageUploading, setIsImageUploading] = useState(false);

  useEffect(() => {
    getCategoryList();
  }, []);

  useEffect(() => {
    if (updatedProduct.attribute) {
      try {
        const data = JSON.parse(updatedProduct.attribute);
        const list = [];
        for (const [key, value] of Object.entries(data)) {
          list.push({ key, value });
        }
        if (list.length === 0) {
          list.push({ key: '', value: '' });
        }
        setInputs(list);

        // Set form field values for attributes
        const formValues = {};
        list.forEach((item, index) => {
          formValues[`key_${index}`] = item.key;
          formValues[`value_${index}`] = item.value;
        });
        editForm.setFieldsValue(formValues);
      } catch (error) {
        console.error('Error parsing attributes:', error);
        setInputs([{ key: '', value: '' }]);
      }
    } else {
      setInputs([{ key: '', value: '' }]);
    }
    // Update productImages when product changes
    setProductImages(updatedProduct.productImages || []);
  }, [product, editForm]);

  async function getCategoryList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_CATEGORY_LIST,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setProductCategoryList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  function addField() {
    setInputs([...inputs, { key: '', value: '' }]);
  }

  const handleModalClose = () => {
    // Reset form and inputs when modal is closed
    editForm.resetFields();
    setInputs([{ key: '', value: '' }]);
    setFileList([]);
    handleEditModelCloseClick();
  };

  async function handleDeleteImageClick(item) {
    try {
      // Check if this is a locally uploaded image (has a local prefix)
      const isLocalImage =
        item.productImageId &&
        item.productImageId.toString().startsWith('local_');

      if (isLocalImage) {
        // For locally uploaded images, just remove from local state
        const updatedImages = productImages.filter(
          (img) => img.productImageId !== item.productImageId,
        );
        setProductImages(updatedImages);
        updatedProduct = {
          ...updatedProduct,
          productImages: updatedImages,
        };
        message.success('Image removed successfully');
        return;
      }

      // For server images, make API call to delete
      const res = await axiosDeleteCall({
        apiEndPoint: `${API_END_POINT.DELETE_PRODUCT_IMAGE}?ProductId=${product.productId}&&ImageId=${item.productImageId}`,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.hideErrorFromUser) {
        message.success(res.data.message);
        // Update local state to remove the deleted image
        const updatedImages = productImages.filter(
          (img) => img.productImageId !== item.productImageId,
        );
        setProductImages(updatedImages);
        // Update the updatedProduct object to reflect the changes
        updatedProduct = {
          ...updatedProduct,
          productImages: updatedImages,
        };
        callback();
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      console.error('Delete image error:', error);
      message.error('Failed to delete image. Please try again.');
    }
  }

  const removeField = (index) => {
    if (inputs.length > 1) {
      const newInputs = [...inputs];
      newInputs.splice(index, 1);
      setInputs(newInputs);

      // Remove form field values for the deleted index
      const currentValues = editForm.getFieldsValue();
      const newFormValues = {};

      // Rebuild form values with correct indices
      newInputs.forEach((item, newIndex) => {
        if (newIndex < index) {
          // Keep existing values for fields before the deleted one
          newFormValues[`key_${newIndex}`] = currentValues[`key_${newIndex}`];
          newFormValues[`value_${newIndex}`] =
            currentValues[`value_${newIndex}`];
        } else {
          // Shift values from fields after the deleted one
          newFormValues[`key_${newIndex}`] =
            currentValues[`key_${newIndex + 1}`] || '';
          newFormValues[`value_${newIndex}`] =
            currentValues[`value_${newIndex + 1}`] || '';
        }
      });

      editForm.setFieldsValue(newFormValues);
    } else {
      // If only one field, just clear it
      setInputs([{ key: '', value: '' }]);
      editForm.setFieldsValue({
        key_0: '',
        value_0: '',
      });
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: async (file) => {
      if (fileList.length < 10) {
        setFileList([...fileList, file]);
        // Automatically upload the image when selected
        try {
          setIsImageUploading(true);
          const uploadedImage = await uploadFile(
            file,
            S3ImageFolder.Product,
            token,
          );

          // Add the uploaded image to the product
          const res = await axiosPostCall({
            apiEndPoint: API_END_POINT.ADD_PRODUCT_IMAGE,
            header: {
              Authorization: `Bearer ${token}`,
            },
            body: {
              productId: product.productId,
              imagePath: [uploadedImage],
            },
          });

          if (res?.hideErrorFromUser) {
            message.success('Image uploaded successfully');
            // Update the local state to show the new image immediately
            const newImage = {
              productImageId: res.data.imageId || `local_${Date.now()}`, // Use server ID or local prefix
              imagePath: uploadedImage,
            };
            const updatedImages = [...productImages, newImage];
            setProductImages(updatedImages);
            // Update the updatedProduct object to reflect the changes
            updatedProduct = {
              ...updatedProduct,
              productImages: updatedImages,
            };
            // Remove the uploaded file from the upload preview list
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
            callback(); // Refresh the product images
          } else {
            message.error(
              res?.error?.message || 'Failed to add image to product',
            );
            // Remove the file from fileList if upload fails
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
          }
          setIsImageUploading(false);
        } catch (error) {
          setIsImageUploading(false);
          console.error('Image upload error:', error);

          // Provide more specific error messages based on error type
          let errorMessage = 'Failed to upload image';
          if (error.response) {
            // Server responded with error status
            if (error.response.status === 413) {
              errorMessage =
                'Image file is too large. Please select a smaller image.';
            } else if (error.response.status === 415) {
              errorMessage =
                'Invalid image format. Please select a valid image file.';
            } else if (error.response.status >= 500) {
              errorMessage =
                'Server error occurred while uploading image. Please try again.';
            } else if (error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else if (error.request) {
            // Network error
            errorMessage =
              'Network error. Please check your internet connection and try again.';
          } else if (error.message) {
            // Other error
            errorMessage = `Upload failed: ${error.message}`;
          }

          message.error(errorMessage);

          // Remove the file from fileList if upload fails
          const index = fileList.indexOf(file);
          const newFileList = fileList.slice();
          newFileList.splice(index, 1);
          setFileList(newFileList);
        }
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  const handleSaveClick = async () => {
    try {
      // Validate form before proceeding
      const formValues = await editForm.validateFields();
      const {
        productName,
        modelNo,
        description,
        weight,
        featured,
        gstTax,
        bodyMaterial,
        p1,
        p2,
        p3,
        r1,
        stock,
        productCategoryId,
        youtubeLink,
      } = formValues;

      // Build attributes object with proper validation
      const data = {};
      for (let index = 0; index < inputs.length; index++) {
        const key = formValues[`key_${index}`];
        const value = formValues[`value_${index}`];

        // Only include non-empty key-value pairs
        if (key && key.trim() && value && value.trim()) {
          data[key.trim()] = value.trim();
        }
      }

      setIsLoading(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.UPDATE_PRODUCT,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          productId: product.productId,
          productName: productName,
          modelNo: modelNo,
          description: description,
          productCategoryId: productCategoryId,
          weight: weight,
          gstTax: gstTax,
          bodyMaterial: bodyMaterial,
          prices: {
            P1: p1,
            P2: p2,
            P3: p3,
            R1: r1,
          },
          sku: stock,
          attribute: JSON.stringify(data),
          featured: featured,
          isActive: true,
          userName: '',
          youtubeLink,
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        setIsLoading(false);
        handleModalClose();
        getAllProduct();
        return;
      } else {
        message.error(res?.error?.message || res.message);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error saving product:', error);

      // Handle form validation errors
      if (error.errorFields) {
        message.error('Please fill in all required fields correctly');
      } else {
        message.error(SOMETHING_WENT_WRONG);
      }
    }
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleModalClose}
        closable={false}
        footer={null}
      >
        <div className='flex flex-col space-y-4'>
          <Row className='font-bold text-2xl self-center'>Edit Product</Row>
          <div className='font-bold'>Images:</div>
          {productImages.length > 0 && (
            <div className='flex flex-row flex-wrap'>
              {productImages.map((item) => (
                <div
                  key={item.productImageId}
                  className='w-24 h-24 flex flex-col justify-between items-center m-2'
                >
                  <img
                    src={item.imagePath}
                    alt={`image_${item.productImageId}`}
                    className=' w-20 h-20 object-contain border border-black'
                  />
                  <DeleteOutlined
                    className='pt-2'
                    onClick={() => handleDeleteImageClick(item)}
                  />
                </div>
              ))}
            </div>
          )}
          <div className='flex flex-col'>
            <div className='flex flex-row space-x-3'>
              <div className='font-bold'>Add Product Image:</div>
              <Upload
                accept='image/*'
                {...uploadProps}
                multiple={false}
                maxCount={10}
                disabled={isImageUploading}
              >
                <Button
                  type='dashed'
                  disabled={isImageUploading}
                  icon={<UploadOutlined />}
                  className='border-primary text-primary'
                >
                  {isImageUploading ? 'Uploading...' : 'Click to Upload'}
                </Button>
              </Upload>
            </div>
          </div>
          <hr />
          <Form
            form={editForm}
            initialValues={updatedProduct}
          >
            <Form.Item
              label='Product Name'
              name='productName'
              rules={[
                { required: true, message: 'Please enter product name' },
                {
                  min: 2,
                  message: 'Product name must be at least 2 characters',
                },
                {
                  max: 100,
                  message: 'Product name cannot exceed 100 characters',
                },
              ]}
            >
              <Input
                name='productName'
                placeholder='Enter product name'
              />
            </Form.Item>
            <Form.Item
              label='Model Number'
              name='modelNo'
              rules={[
                { required: true, message: 'Please enter model number' },
                {
                  max: 50,
                  message: 'Model number cannot exceed 50 characters',
                },
              ]}
            >
              <Input
                name='modelNo'
                placeholder='Enter model number'
              />
            </Form.Item>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='Weight'
                name='weight'
                rules={[
                  { required: true, message: 'Please enter weight' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Weight cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='weight'
                  min={0}
                  placeholder='Enter weight'
                />
              </Form.Item>
              <Form.Item
                label='GST'
                name='gstTax'
                rules={[
                  { required: true, message: 'Please enter GST percentage' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('GST cannot be negative'),
                        );
                      }
                      if (value && value > 100) {
                        return Promise.reject(
                          new Error('GST cannot exceed 100%'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='gstTax'
                  min={0}
                  max={100}
                  placeholder='Enter GST %'
                />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='Stock'
                name='stock'
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Stock cannot be negative'),
                        );
                      }
                      if (value && !Number.isInteger(value)) {
                        return Promise.reject(
                          new Error('Stock must be a whole number'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='stock'
                  min={0}
                  placeholder='Enter stock quantity'
                />
              </Form.Item>
            </Row>
            <span className='my-1 font-bold text-lg'>Prices:</span>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P1'
                name='p1'
                rules={[
                  { required: true, message: 'Please enter P1 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='p1'
                  min={0}
                  placeholder='Enter P1 price'
                />
              </Form.Item>
              <Form.Item
                label='P2'
                name='p2'
                rules={[
                  { required: true, message: 'Please enter P2 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='p2'
                  min={0}
                  placeholder='Enter P2 price'
                />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P3'
                name='p3'
                rules={[
                  { required: true, message: 'Please enter P3 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='p3'
                  min={0}
                  placeholder='Enter P3 price'
                />
              </Form.Item>
              <Form.Item
                label='R1'
                name='r1'
                rules={[
                  { required: true, message: 'Please enter R1 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='r1'
                  min={0}
                  placeholder='Enter R1 price'
                />
              </Form.Item>
            </Row>
            <Form.Item
              label='Body Material'
              name='bodyMaterial'
              rules={[
                { required: true, message: 'Please enter body material' },
                {
                  max: 100,
                  message: 'Body material cannot exceed 100 characters',
                },
              ]}
            >
              <Input
                name='bodyMaterial'
                placeholder='Enter body material'
              />
            </Form.Item>
            <Form.Item
              label='Description'
              name='description'
              rules={[
                { required: true, message: 'Please enter product description' },
                {
                  max: 1000,
                  message: 'Description cannot exceed 1000 characters',
                },
              ]}
            >
              <TextArea
                name='description'
                rows={4}
                placeholder='Enter product description'
                showCount
                maxLength={1000}
              />
            </Form.Item>

            <Form.Item
              label='Product Category'
              name='productCategoryId'
              rules={[
                { required: true, message: 'Please select a product category' },
              ]}
            >
              <Select placeholder='Select a Product Category'>
                {productCategoryList.map((category) => (
                  <Option
                    key={category.productCategoryId}
                    value={category.productCategoryId}
                  >
                    {category.categoryName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {inputs.map((item, index) => (
              <div
                className='flex flex-row justify-between items-start'
                key={`item_${index}`}
              >
                <Form.Item
                  rules={[{ required: true, message: 'Please input a key' }]}
                  name={`key_${index}`}
                >
                  <Input placeholder='Enter key' />
                </Form.Item>
                <Form.Item
                  rules={[{ required: true, message: 'Please input a value' }]}
                  name={`value_${index}`}
                >
                  <Input placeholder='Enter value' />
                </Form.Item>
                <Button
                  type='default'
                  danger
                  onClick={() => removeField(index)}
                >
                  <DeleteOutlined />
                </Button>
                <Button
                  type='dashed'
                  onClick={addField}
                  className='border-primary text-primary'
                >
                  <PlusOutlined />
                </Button>
              </div>
            ))}
            <Form.Item
              label='Featured'
              name='featured'
            >
              <Switch name='featured' />
            </Form.Item>
            <Form.Item
              label='Youtube Link'
              name='youtubeLink'
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value.trim()) {
                      const youtubeRegex =
                        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
                      if (!youtubeRegex.test(value)) {
                        return Promise.reject(
                          new Error('Please enter a valid YouTube URL'),
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                name='youtubeLink'
                placeholder='Enter YouTube URL'
              />
            </Form.Item>
          </Form>
          <div className='flex flex-row justify-around'>
            <Button
              onClick={handleModalClose}
              className='w-32 self-center bg-surface border border-border text-text hover:text-primary hover:border-primary'
              disabled={isLoading}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveClick}
              className='w-32 self-center border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
              type='primary'
              disabled={isLoading || isImageUploading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default EditProductModel;
